import Container from 'components/container/container';
import LegalPost from 'components/legal/legal-post';
import Link from 'next/link';

import { DEVELOPMENT_MAILTO, LIBUM_URL } from 'shared/constants/globals';
import { LEGAL_DOCS } from 'shared/data/legal-docs';

const DOC = LEGAL_DOCS.find((d) => d.id === 'poweron-pipelines')!;

export default function PowerOnPipelinesEULA() {
  const title = DOC.title;
  const description = DOC.description;
  const keywords = DOC.keywords.join(',');
  const url = `${LIBUM_URL}/legal/${DOC.id}}`;

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="legal">
      <div style={{ width: '100%' }}>
        <LegalPost document={DOC}>
          <h1 className="anchor" id="agreement-to-terms">
            <Link href="#agreement-to-terms">1. Agreement to Terms</Link>
          </h1>
          <p>
            This End User License Agreement (<b>&quot;Agreement&quot;</b>) is a legal agreement between you (either as
            an individual or on behalf of an entity) (<b>&quot;User&quot;</b>) and Libum (<b>&quot;Company&quot;</b>,{' '}
            <b>&quot;we&quot;</b>, <b>&quot;us&quot;</b>, or <b>&quot;our&quot;</b>) regarding your use of PowerOn
            Pipelines (<b>&quot;Software&quot;</b>). By downloading, installing, or using the Software, you agree to be
            bound by the terms of this Agreement. If you do not agree to the terms of this Agreement, do not install or
            use the Software.
          </p>
          <h1 className="anchor" id="license-grant">
            <Link href="#license-grant">2. License Grant</Link>
          </h1>
          <p>
            Subject to the terms of this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable
            license to install and use the Software for personal or business use in accordance with the permitted number
            of users or devices specified in your Proof of License. The Software is provided under a license and is not
            sold. A valid paid subscription and Software License Key are required to access and use the Software.
            Licenses are subject to restrictions based on the Proof of License, including the number of authorized users
            and devices.
          </p>
          <h1 className="anchor" id="paid-license-only">
            <Link href="#paid-license-only">3. Paid License Only</Link>
          </h1>
          <p>
            Access to the Software requires a paid subscription. The license is granted on a subscription basis, and
            continued access is contingent on timely renewal and compliance with the Proof of License. There is no free
            or evaluation version of the Software.
          </p>
          <h1 className="anchor" id="proof-of-license">
            <Link href="#proof-of-license">4. Proof of License</Link>
          </h1>
          <p>Your Proof of License specifies:</p>
          <ul>
            <li>The total number of authorized hosts.</li>
            <li>The license term (subscription duration).</li>
            <li>Any applicable terms or restrictions related to usage.</li>
          </ul>
          Failure to maintain a valid Proof of License may result in termination of access to the Software.
          <h1 className="anchor" id="restrictions">
            <Link href="#restrictions">5. Restrictions</Link>
          </h1>
          <p>You may not:</p>
          <ul>
            <li>Modify, adapt, translate, or create derivative works of the Software.</li>
            <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Software.</li>
            <li>Rent, lease, sublicense, sell, or distribute the Software to third parties.</li>
            <li>Use the Software in violation of any applicable law or regulation.</li>
            <li>Remove or alter any proprietary notices or marks on the Software.</li>
            <li>
              Share, transfer, or allow unauthorized users to access the Software beyond the permitted number of user
              seats.
            </li>
          </ul>
          <h1 className="anchor" id="transfer-of-license">
            <Link href="#transfer-of-license">6. Transfer of License</Link>
          </h1>
          <p>
            You may transfer the Software to a different internal workstation or user as long as you have a valid
            license for each such transfer. You may not transfer the Software to a third party or install it on a system
            not owned or controlled by you.
          </p>
          <h1 className="anchor" id="intellectual-property-rights">
            <Link href="#intellectual-property-rights">7. Intellectual Property Rights</Link>
          </h1>
          <p>
            The Software and all related intellectual property rights are owned by Libum or its licensors. This
            Agreement does not grant you any ownership rights in the Software.
          </p>
          <h1 className="anchor" id="updates-and-support">
            <Link href="#updates-and-support">8. Updates and Support</Link>
          </h1>
          <p>
            We may provide updates, patches, or enhancements to the Software at our discretion. Continued use of the
            Software after an update constitutes acceptance of the modified terms.
          </p>
          <p>
            During the term of your license, Libum will provide support services and maintenance to you in accordance
            with Libum’s then-current support terms and conditions, as furthered detailed on{' '}
            <Link href="https://libum.io/legal/sla">https://libum.io/legal/sla</Link> (the{' '}
            <b>&quot;Service Level Agreement&quot;</b>), subject to your payment of the applicable fees. By accepting
            the terms of this EULA, you are accepting the Service Level Agreement, which terms are hereby incorporated
            by reference.
          </p>
          <p>
            We reserve the right to audit your usage of the Software to ensure compliance with the terms of this
            Agreement and the Proof of License.
          </p>
          <h1 className="anchor" id="disclaimer-of-warranties">
            <Link href="#disclaimer-of-warranties">9. Disclaimer of Warranties</Link>
          </h1>
          <p>
            THE SOFTWARE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY
            LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <h1 className="anchor" id="limitation-of-liability">
            <Link href="#limitation-of-liability">10. Limitation of Liability</Link>
          </h1>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO THE USE OR INABILITY TO USE THE
            SOFTWARE.
          </p>
          <h1 className="anchor" id="termination">
            <Link href="#termination">11. Termination</Link>
          </h1>
          <p>
            This Agreement is effective until terminated. We may terminate this Agreement at any time if you fail to
            comply with its terms. Upon termination, you must cease all use of the Software and delete all copies. If
            your license is subscription-based, failure to renew or pay applicable fees may result in automatic
            termination of your access to the Software. Any attempt to circumvent licensing or user seat limitations
            will result in immediate termination.
          </p>
          <h1 className="anchor" id="governing-law">
            <Link href="#governing-law">12. Governing Law</Link>
          </h1>
          <p>
            This Agreement shall be governed by and construed in accordance with the laws of Texas, without regard to
            its conflict of law principles.
          </p>
          <p>
            The failure of Libum to exercise or enforce any right or provision of this Agreement shall not constitute a
            waiver of such right or provision.
          </p>
          <h1 className="anchor" id="contact-information">
            <Link href="#contact-information">13. Contact Information</Link>
          </h1>
          <p>
            If you have any questions about this Agreement, please contact us at: Libum (
            <Link href={DEVELOPMENT_MAILTO}>development@libum.io</Link>)
          </p>
          <p>
            <i>
              By using the Software, you acknowledge that you have read, understood, and agreed to the terms of this
              Agreement.
            </i>
          </p>
        </LegalPost>
      </div>
    </Container>
  );
}
